// src/components/dashboard/MonthYearSelector.tsx

import React, { useState, useEffect, useRef } from 'react';
import { Calendar, ChevronLeft, ChevronRight, Calendar as CalendarIcon } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext.tsx';
import { getDashboardThemeClasses } from './dashboard/dashboardTheme.ts';

interface MonthYearSelectorProps {
  value: string; // formato YYYY-MM
  onChange: (value: string) => void;
  className?: string;
  error?: boolean;
}

const MonthYearSelector: React.FC<MonthYearSelectorProps> = ({
  value,
  onChange,
  className = '',
  error = false
}) => {
  const { isDark } = useTheme();
  const theme = getDashboardThemeClasses(isDark);
  const [isOpen, setIsOpen] = useState(false);
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Parse o valor inicial para obter ano e mês
  useEffect(() => {
    if (value) {
      const [yearStr, monthStr] = value.split('-');
      if (yearStr && !isNaN(parseInt(yearStr))) {
        setYear(parseInt(yearStr));
      }
    }
  }, [value]);

  // Fechar o dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Formatar a data para exibição
  const formatDisplayDate = (dateStr: string): string => {
    if (!dateStr) return '';
    
    const months = [
      'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
      'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
    ];
    
    const [yearStr, monthStr] = dateStr.split('-');
    const monthIndex = parseInt(monthStr) - 1;
    
    return `${months[monthIndex]} de ${yearStr}`;
  };

  // Navegar para o ano anterior
  const prevYear = () => {
    setYear(year - 1);
  };

  // Navegar para o próximo ano
  const nextYear = () => {
    setYear(year + 1);
  };

  // Selecionar um mês
  const selectMonth = (month: number) => {
    // Mês deve ser uma string de dois dígitos (01-12)
    const monthStr = String(month).padStart(2, '0');
    const newValue = `${year}-${monthStr}`;
    onChange(newValue);
    setIsOpen(false);
  };

  // Verificar se um mês está selecionado
  const isMonthSelected = (month: number): boolean => {
    if (!value) return false;
    
    const [yearStr, monthStr] = value.split('-');
    return parseInt(yearStr) === year && parseInt(monthStr) === month;
  };

  // Obter o mês atual
  const getCurrentMonth = (): number => {
    const today = new Date();
    return today.getMonth() + 1; // JavaScript meses são 0-indexed
  };

  // Verificar se é o mês atual
  const isCurrentMonth = (month: number): boolean => {
    const today = new Date();
    return today.getFullYear() === year && (today.getMonth() + 1) === month;
  };

  // Array com os meses
  const months = [
    { value: 1, label: 'Jan' },
    { value: 2, label: 'Fev' },
    { value: 3, label: 'Mar' },
    { value: 4, label: 'Abr' },
    { value: 5, label: 'Mai' },
    { value: 6, label: 'Jun' },
    { value: 7, label: 'Jul' },
    { value: 8, label: 'Ago' },
    { value: 9, label: 'Set' },
    { value: 10, label: 'Out' },
    { value: 11, label: 'Nov' },
    { value: 12, label: 'Dez' }
  ];

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      {/* Campo de exibição */}
      <div 
        className={`flex cursor-pointer items-center justify-between rounded-xl border px-3 py-2 transition-colors ${error ? 'border-rose-300' : theme.border} ${theme.input}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center">
          <CalendarIcon className={`mr-2 h-5 w-5 ${theme.mutedText}`} />
          <span className={theme.primaryText}>{formatDisplayDate(value) || 'Selecionar mês/ano'}</span>
        </div>
        <ChevronRight className={`h-5 w-5 transition-transform ${theme.mutedText} ${isOpen ? 'rotate-90 transform' : ''}`} />
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className={`fixed z-50 mt-1 w-64 rounded-xl border shadow-lg ${theme.surface}`} style={{
          top: dropdownRef.current ? dropdownRef.current.getBoundingClientRect().bottom + 4 : 0,
          left: dropdownRef.current ? dropdownRef.current.getBoundingClientRect().left : 0
        }}>
          {/* Header - Seletor de ano */}
          <div className={`flex items-center justify-between border-b p-3 ${theme.border}`}>
            <button 
              onClick={prevYear}
              className={`rounded-full p-1 transition-colors ${isDark ? 'text-[#A6B2C3] hover:bg-[#27364D]/35' : 'text-sminy-text-secondary hover:bg-slate-100'}`}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className={`font-medium ${theme.primaryText}`}>{year}</span>
            <button 
              onClick={nextYear}
              className={`rounded-full p-1 transition-colors ${isDark ? 'text-[#A6B2C3] hover:bg-[#27364D]/35' : 'text-sminy-text-secondary hover:bg-slate-100'}`}
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Grid de meses */}
          <div className="grid grid-cols-4 gap-1 p-2">
            {months.map((month) => (
              <button
                key={month.value}
                onClick={() => selectMonth(month.value)}
                className={`
                  p-2 text-sm rounded-md transition-colors
                  ${isMonthSelected(month.value) 
                    ? 'bg-indigo-600 text-white' 
                      : isCurrentMonth(month.value)
                      ? isDark ? 'bg-sminy-primary/15 text-[#66A3FF]' : 'bg-sminy-blue-light text-sminy-primary'
                      : isDark ? 'text-[#A6B2C3] hover:bg-[#27364D]/35' : 'text-sminy-text-secondary hover:bg-slate-100'}
                `}
              >
                {month.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MonthYearSelector;
